import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik, type FormikHelpers } from "formik";
import { number, object, string } from "yup";
import {
  IconCashBanknote,
  IconChartLine,
  IconHourglass,
  IconShieldLock,
  IconTrash,
  IconWallet,
} from "@tabler/icons-react";
import moment from "moment/moment";
import { Table } from "../UI/Table";
import Status from "../UI/Status";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import DataCard from "../UI/DataCard";
import Title from "../UI/Title";
import Text from "../UI/Text";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import IconButton from "../UI/IconButton";
import ModalDialog, { ModalDialogFooter } from "../UI/ModalDialog";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { dateConvert } from "../../api/Functions";
import MoisPicker from "./MoisPicker";
import ReactECharts from "echarts-for-react";
import {
  useCourbeTresorerie,
  usePointe,
  PRELEVEMENT_HONORAIRES,
  usePrelevementsHonoraires,
} from "../../hooks/use-compta-pro";
import { euros, periodeCourante, type Periode } from "./comptaUtils";
import styles from "./Compta.module.css";

// ECharts peint sur un canvas et ne résout pas les custom properties CSS :
// ces valeurs doivent rester alignées sur --color-primary (src/styles/tokens.css).
const COURBE_COLOR = "#2875f7";
const COURBE_AREA_COLOR = "rgba(40, 117, 247, 0.12)";

interface PrelevementFormValues {
  date_prelevement: string;
  montant: string;
  comment: string;
}

const prelevementSchema = object({
  date_prelevement: string().required("La date est obligatoire"),
  montant: number()
    .typeError("Montant invalide")
    .positive("Le montant doit être positif")
    .required("Le montant est obligatoire"),
  comment: string().optional(),
});

export default function PointeTab() {
  const toast = useToast();
  const navigate = useNavigate();
  const [periode, setPeriode] = useState<Periode>(periodeCourante());
  const { data, isLoading, mutate } = usePointe(periode.year, periode.month);
  const [showCourbe, setShowCourbe] = useState(false);
  const { data: courbe } = useCourbeTresorerie(
    periode.year,
    periode.month,
    showCourbe,
  );
  const {
    prelevements,
    creerPrelevement,
    supprimerPrelevement,
    mutate: mutatePrelevements,
  } = usePrelevementsHonoraires();

  const [prelevementOpen, setPrelevementOpen] = useState(false);

  const prelevementInitialValues: PrelevementFormValues = {
    date_prelevement: moment().format("YYYY-MM-DD"),
    montant:
      data && parseFloat(data.honoraires_a_prelever) > 0
        ? data.honoraires_a_prelever
        : "",
    comment: "",
  };

  async function submitPrelevement(
    values: PrelevementFormValues,
    { resetForm }: FormikHelpers<PrelevementFormValues>,
  ) {
    try {
      await creerPrelevement({
        type: PRELEVEMENT_HONORAIRES,
        date_transac: values.date_prelevement,
        // Sortie de trésorerie : la saisie est positive, l'écriture est négative.
        montant: -parseFloat(values.montant),
        comment: values.comment || undefined,
      });
      toast.success({ title: "Prélèvement d'honoraires enregistré" });
      setPrelevementOpen(false);
      resetForm();
      await mutate();
    } catch (error) {
      toast.error({
        title: "Enregistrement impossible",
        description: handleAPIError(error),
      });
    }
  }

  async function deletePrelevement(id: number) {
    try {
      await supprimerPrelevement(id);
      // Un prélèvement déjà comptabilisé n'est pas supprimé mais contrepassé : la
      // liste doit repartir du serveur pour montrer l'écriture d'annulation.
      await Promise.all([mutate(), mutatePrelevements()]);
    } catch (error) {
      toast.error({
        title: "Suppression impossible",
        description: handleAPIError(error),
      });
    }
  }

  return (
    <div className={styles.stack}>
      <Formik
        initialValues={prelevementInitialValues}
        enableReinitialize={true}
        validationSchema={prelevementSchema}
        onSubmit={submitPrelevement}
      >
        {({ resetForm }) => (
          <ModalDialog
            open={prelevementOpen}
            onOpenChange={(open) => {
              if (!open) {
                resetForm();
              }
              setPrelevementOpen(open);
            }}
            header="Prélever mes honoraires"
            size="sm"
          >
            <Form className={styles.modalForm}>
              <Text>
                Enregistre le virement de vos honoraires depuis le compte de
                gestion vers le compte de votre entreprise. Le montant est
                déduit de l'indicateur « Honoraires à prélever ».
              </Text>
              <TextInput
                label="Date du virement"
                type="date"
                name="date_prelevement"
                marginForm=""
              />
              <TextInput
                label="Montant (TTC)"
                type="number"
                step="0.01"
                name="montant"
                marginForm=""
              />
              <TextInput
                label="Commentaire"
                name="comment"
                placeholder="Virement honoraires juillet"
                marginForm=""
              />
              <ModalDialogFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    resetForm();
                    setPrelevementOpen(false);
                  }}
                >
                  Annuler
                </Button>
                <FormikSubmitButton>
                  Enregistrer le prélèvement
                </FormikSubmitButton>
              </ModalDialogFooter>
            </Form>
          </ModalDialog>
        )}
      </Formik>
      <div className={styles.toolbar}>
        <MoisPicker
          label="Situation à fin"
          className={styles.moisPicker}
          value={periode}
          onChange={setPeriode}
        />
      </div>
      {isLoading || !data ? (
        <IsLoading />
      ) : (
        <>
          {data.anomalies.length > 0 && (
            <Callout variant="danger" title="Points de contrôle">
              {data.anomalies.join(" — ")}
            </Callout>
          )}
          <div>
            <Button
              variant="tertiary"
              icon={IconChartLine}
              onClick={() => setShowCourbe((visible) => !visible)}
            >
              {showCourbe
                ? "Masquer la pointe de trésorerie"
                : "Afficher la pointe de trésorerie"}
            </Button>
          </div>
          {showCourbe && courbe && courbe.serie.length > 1 && (
            <>
              <Title variant="kilo">Pointe de trésorerie</Title>
              <Text color="muted">
                Solde du compte de gestion jour par jour
                {courbe.pointe_haute && (
                  <>
                    — pointe haute{" "}
                    <strong>{euros(courbe.pointe_haute.solde)}</strong> le{" "}
                    <strong>{dateConvert(courbe.pointe_haute.jour)}</strong>
                  </>
                )}
                {courbe.pointe_basse && (
                  <>
                    , pointe basse{" "}
                    <strong>{euros(courbe.pointe_basse.solde)}</strong> le{" "}
                    <strong>{dateConvert(courbe.pointe_basse.jour)}</strong>
                  </>
                )}
                .
              </Text>
              <ReactECharts
                option={{
                  tooltip: {
                    trigger: "axis",
                    valueFormatter: (value: number) => euros(value),
                  },
                  grid: { left: 80, right: 24, top: 24, bottom: 32 },
                  xAxis: {
                    type: "category",
                    data: courbe.serie.map((point) => dateConvert(point.jour)),
                  },
                  yAxis: {
                    type: "value",
                    axisLabel: { formatter: (value: number) => euros(value) },
                  },
                  series: [
                    {
                      name: "Trésorerie",
                      type: "line",
                      step: "end",
                      symbol: "none",
                      data: courbe.serie.map((point) =>
                        parseFloat(point.solde),
                      ),
                      lineStyle: { color: COURBE_COLOR, width: 2 },
                      itemStyle: { color: COURBE_COLOR },
                      areaStyle: { color: COURBE_AREA_COLOR },
                      markPoint: {
                        data: [
                          { type: "max", name: "Pointe haute" },
                          { type: "min", name: "Pointe basse" },
                        ],
                        label: {
                          formatter: (params: { value: number }) =>
                            euros(params.value),
                          fontSize: 10,
                        },
                      },
                    },
                  ],
                }}
              />
            </>
          )}
          <Title variant="kilo" className={styles.sectionTitle}>
            Contrôle des fonds détenus
          </Title>
          <DataCard.Grid>
            <DataCard
              title="Fonds mandants détenus"
              icon={IconWallet}
              value={euros(data.total_fonds_mandants)}
            />
            <DataCard
              title="Dont réserves de trésorerie"
              icon={IconWallet}
              value={euros(data.total_reserves_mandants)}
              subtitle="Retenues sur les reversements, hors solde reversable"
            />
            <DataCard
              title="Avances aux mandants"
              icon={IconCashBanknote}
              value={euros(data.total_avances_mandants)}
              subtitle="Fonds propres de l'agence à récupérer"
            />
            <DataCard
              title="Dépôts de garantie détenus"
              icon={IconShieldLock}
              value={euros(data.total_depots_garantie)}
            />
            <DataCard
              title="Mouvements en attente d'affectation"
              icon={IconHourglass}
              value={euros(data.solde_attente)}
            />
            <DataCard
              title="Honoraires à prélever"
              icon={IconCashBanknote}
              value={euros(data.honoraires_a_prelever)}
              subtitle={`${euros(data.honoraires_acquis)} acquis — ${euros(
                data.honoraires_preleves,
              )} déjà prélevés`}
              actionLabel="Prélever"
              onAction={() => setPrelevementOpen(true)}
            />
          </DataCard.Grid>
          <Title variant="kilo" className={styles.sectionTitle}>
            Comptes bancaires de gestion
          </Title>
          {data.banques.length === 0 && (
            <Callout variant="info">
              Aucun compte bancaire connecté : les encaissements et reversements
              sont suivis via le compte interne 580. Connectez la banque de
              votre compte de gestion (le compte séparé où transitent les fonds
              de vos mandants) puis cochez « Compte de gestion » dans ses
              réglages pour activer le rapprochement automatique entre solde
              comptable et solde bancaire — la première vérification du contrôle
              des fonds.
            </Callout>
          )}
          {data.banques.length > 0 && (
            <Table variant="card">
              <Table.Header>
                <Table.Column>Compte</Table.Column>
                <Table.Column>Banque</Table.Column>
                <Table.Column align="right">Solde comptable</Table.Column>
                <Table.Column align="right">Solde bancaire</Table.Column>
                <Table.Column align="right">Écart</Table.Column>
              </Table.Header>
              <Table.Body>
                {data.banques.map((banque) => (
                  <Table.Row key={banque.numero}>
                    <Table.Cell>{banque.numero}</Table.Cell>
                    <Table.Cell>{banque.label}</Table.Cell>
                    <Table.Cell align="right">
                      {euros(banque.solde_comptable)}
                    </Table.Cell>
                    <Table.Cell align="right">
                      {euros(banque.solde_banque)}
                    </Table.Cell>
                    <Table.Cell align="right">
                      {banque.ecart === null ? (
                        "—"
                      ) : (
                        <Status
                          color={
                            parseFloat(banque.ecart) === 0
                              ? "success"
                              : "warning"
                          }
                          text={euros(banque.ecart)}
                        />
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
          <Title variant="kilo" className={styles.sectionTitle}>
            Ventilation par mandant
          </Title>
          {data.mandants.length === 0 ? (
            <Callout variant="info">
              Aucun mandant mouvementé sur la plage. Créez un mandat de gestion
              dans l'onglet Mandats.
            </Callout>
          ) : (
            <Table variant="card">
              <Table.Header>
                <Table.Column>Compte</Table.Column>
                <Table.Column>Mandant</Table.Column>
                <Table.Column align="right">Solde dû</Table.Column>
                <Table.Column>Situation</Table.Column>
              </Table.Header>
              <Table.Body>
                {data.mandants.map((mandant) => (
                  <Table.Row
                    key={mandant.numero}
                    className={styles.clickableRow}
                    onClick={() =>
                      navigate(
                        `/bailleur/profiles/${parseInt(
                          mandant.numero.slice(3),
                        )}?tab-bailleur-show=comptabilite`,
                      )
                    }
                  >
                    <Table.Cell>{mandant.numero}</Table.Cell>
                    <Table.Cell>{mandant.label}</Table.Cell>
                    <Table.Cell align="right">
                      {euros(mandant.solde)}
                    </Table.Cell>
                    <Table.Cell>
                      <Status
                        color={
                          parseFloat(mandant.solde) < 0 ? "danger" : "success"
                        }
                        text={
                          parseFloat(mandant.solde) < 0
                            ? "Débiteur"
                            : "Créditeur"
                        }
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
          {data.locataires_crediteurs.length > 0 && (
            <>
              <Title variant="kilo" className={styles.sectionTitle}>
                Locataires créditeurs (trop-perçus)
              </Title>
              <Table variant="card">
                <Table.Header>
                  <Table.Column>Compte</Table.Column>
                  <Table.Column>Locataire</Table.Column>
                  <Table.Column align="right">Trop-perçu</Table.Column>
                </Table.Header>
                <Table.Body>
                  {data.locataires_crediteurs.map((locataire) => (
                    <Table.Row
                      key={locataire.numero}
                      className={styles.clickableRow}
                      onClick={() =>
                        navigate(
                          `/bailleur/tenants/${parseInt(
                            locataire.numero.slice(3),
                          )}?tab-tenant-show=historique-transactions`,
                        )
                      }
                    >
                      <Table.Cell>{locataire.numero}</Table.Cell>
                      <Table.Cell>{locataire.label}</Table.Cell>
                      <Table.Cell align="right">
                        {euros(-parseFloat(locataire.solde))}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}
          {prelevements.length > 0 && (
            <>
              <Title variant="kilo" className={styles.sectionTitle}>
                Prélèvements d'honoraires
              </Title>
              <Table variant="card">
                <Table.Header>
                  <Table.Column>Date</Table.Column>
                  <Table.Column align="right">Montant</Table.Column>
                  <Table.Column>Commentaire</Table.Column>
                  <Table.Column align="right"></Table.Column>
                </Table.Header>
                <Table.Body>
                  {prelevements.map((prelevement) => (
                    <Table.Row key={prelevement.id}>
                      <Table.Cell>
                        {dateConvert(prelevement.date_transac)}
                      </Table.Cell>
                      <Table.Cell align="right">
                        {euros(prelevement.montant)}
                      </Table.Cell>
                      <Table.Cell>{prelevement.comment ?? "—"}</Table.Cell>
                      <Table.Cell align="right">
                        <div className={styles.action}>
                          <IconButton
                            icon={IconTrash}
                            label="Supprimer le prélèvement"
                            onClick={() => deletePrelevement(prelevement.id)}
                          />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}
        </>
      )}
    </div>
  );
}
